import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { fmtNum } from "../lib/csv.ts";
import { theme } from "../theme.ts";
import type { DemandBreakdownResponse } from "@march/shared";

/** FY label ("FY2026") -> month range, Jul..Jun, capped at the current month. */
export function fyMonths(fy: string): { from: string; to: string } {
  const end = Number(fy.replace("FY", ""));
  const from = `${end - 1}-07`;
  const fyTo = `${end}-06`;
  const now = new Date();
  const cur = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  return { from, to: fyTo < cur ? fyTo : cur };
}

/**
 * "How this number is built" — decomposes a demand total into parts a user
 * can look up in NetSuite themselves: units sold on their own (what Sales by
 * Item shows), units inside each umbrella model, and units shipped free.
 *
 * The parts come from the same ingestion pass as the total, and the API checks
 * them against it, so this panel can state outright whether they reconcile.
 */
export function BreakdownPanel({
  itemid,
  fy,
  onClose,
}: {
  itemid: string;
  fy: string;
  onClose: () => void;
}) {
  const { from, to } = fyMonths(fy);
  const q = useQuery<DemandBreakdownResponse>({
    queryKey: ["breakdown", itemid, from, to],
    queryFn: () =>
      apiGet<DemandBreakdownResponse>(
        `/api/history/${encodeURIComponent(itemid)}/breakdown?from=${from}&to=${to}`,
      ),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const d = q.data;
  const line = (label: React.ReactNode, qty: number, note?: React.ReactNode, strong = false) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: theme.space.md,
        padding: "8px 0",
        borderBottom: `1px solid ${theme.color.border}`,
        alignItems: "baseline",
      }}
    >
      <div>
        <div style={{ fontWeight: strong ? 700 : 500 }}>{label}</div>
        {note && (
          <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted, marginTop: 2 }}>
            {note}
          </div>
        )}
      </div>
      <div
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: strong ? 700 : 600,
          fontSize: strong ? 16 : 14,
        }}
      >
        {fmtNum(qty)}
      </div>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`How ${itemid} ${fy} demand is built`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: theme.color.scrim,
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 100%)",
          height: "100%",
          overflowY: "auto",
          background: theme.color.surface,
          boxShadow: theme.shadow.lg,
          padding: theme.space.xl,
          fontSize: theme.font.size.sm,
          color: theme.color.text,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
          <div>
            <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted }}>
              How this number is built · {fy} ({from} → {to})
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{itemid}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: `1px solid ${theme.color.border}`,
              background: theme.color.surface,
              borderRadius: theme.radius.md,
              padding: "4px 10px",
              cursor: "pointer",
              color: theme.color.textMuted,
            }}
          >
            ✕
          </button>
        </div>

        {q.isLoading && <div style={{ marginTop: 24, color: theme.color.textMuted }}>Loading…</div>}
        {q.error && (
          <div style={{ marginTop: 24, color: theme.color.danger }}>{(q.error as Error).message}</div>
        )}

        {d && !d.has_attribution && (
          <div
            style={{
              marginTop: 20,
              padding: "10px 12px",
              background: theme.color.warningFaint,
              borderRadius: theme.radius.md,
              color: theme.color.warningText,
            }}
          >
            No breakdown has been recorded for this period yet. It is written by the full
            monthly refresh — closed months fill in on the next backfill, and the current
            month overnight.
          </div>
        )}

        {d && d.has_attribution && (
          <div style={{ marginTop: 20 }}>
            {line(
              "Sold on its own",
              d.sold_alone,
              "Revenue lines for this SKU — the only part NetSuite's Sales by Item report shows.",
            )}
            {line(
              `Inside umbrellas · ${d.inside_umbrellas.models} model${d.inside_umbrellas.models === 1 ? "" : "s"}`,
              d.inside_umbrellas.total,
              "Each model below can be checked in NetSuite by looking up its own sales for the same period.",
            )}
            {d.inside_umbrellas.top.length > 0 && (
              <div style={{ padding: "4px 0 8px 12px", borderBottom: `1px solid ${theme.color.border}` }}>
                {d.inside_umbrellas.top.map((m) => (
                  <div
                    key={m.umbrella}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      padding: "3px 0",
                      color: theme.color.textMuted,
                      fontSize: theme.font.size.xs,
                    }}
                  >
                    <span style={{ fontFamily: "monospace" }}>{m.umbrella}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtNum(m.qty)}</span>
                  </div>
                ))}
                {d.inside_umbrellas.other.models > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      padding: "3px 0",
                      color: theme.color.textFaint,
                      fontSize: theme.font.size.xs,
                      fontStyle: "italic",
                    }}
                  >
                    <span>+ {d.inside_umbrellas.other.models} more models</span>
                    <span>{fmtNum(d.inside_umbrellas.other.qty)}</span>
                  </div>
                )}
              </div>
            )}
            {d.shipped_free !== 0 &&
              line(
                "Shipped free, no umbrella on the invoice",
                d.shipped_free,
                "$0 lines on customer orders — typically logo or programme shipments.",
              )}
            {line("Total used", d.total_attributed, undefined, true)}

            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: theme.radius.md,
                background: d.reconciles ? theme.color.successFaint : theme.color.warningFaint,
                color: d.reconciles ? theme.color.successText : theme.color.warningText,
              }}
            >
              {d.reconciles ? (
                <>
                  ✓ Adds up exactly to the {fmtNum(d.total_stored)} shown in the table. These parts
                  come from the same calculation as the total, not a separate estimate.
                </>
              ) : (
                <>
                  Parts total {fmtNum(d.total_attributed)} against {fmtNum(d.total_stored)} in the
                  table. For the current month this is expected — the breakdown refreshes overnight
                  while the total updates hourly. For a closed period, it means something needs
                  looking at.
                </>
              )}
            </div>
            <div style={{ marginTop: 12, fontSize: theme.font.size.xs, color: theme.color.textMuted }}>
              For invoice-level detail, open this SKU in the <b>Single SKU</b> view and expand any
              month.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
